// Org-wide pending follow-ups list. Complements ReminderOverlay (which only
// pops within ±2h of scheduledAt) by showing every pending follow-up sorted
// by when it's due — overdue, today, upcoming. Each row has the same action
// surface as the popup (Done / Snooze) plus Reschedule and Clear.

import React, { useMemo, useState } from "react";
import {
  Loader2,
  AlertCircle,
  RefreshCw,
  Bell,
  Check,
  Moon,
  Pencil,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Mail,
} from "lucide-react";
import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import { Tooltip } from "antd";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import toast from "react-hot-toast";
import { Button, Badge } from "../atoms";
import PageHeader from "../organisms/PageHeader";
import TableFooter from "../organisms/TableFooter";
import FollowUpScheduler from "../molecules/FollowUpScheduler";
import { useOrgCurrency } from "../hooks/useOrgCurrency";
import { formatCurrency as libFormatCurrency } from "../lib/currency";
import {
  usePendingFollowUps,
  useCompleteFollowUp,
  useSnoozeFollowUp,
  useClearFollowUp,
} from "../hooks/useFollowUps";
import EmailFollowUpsPanel from "./EmailFollowUpsPanel";

dayjs.extend(relativeTime);

const PAGE_SIZE = 20;

const SCOPE_OPTIONS = [
  { id: "mine", label: "Mine" },
  { id: "org", label: "Everyone" },
];

const VIEW_TABS = [
  { id: "email", label: "Email monitoring", icon: Mail },
  { id: "manual", label: "Manual reminders", icon: Bell },
];

const statusTone = { open: "primary", won: "success", lost: "danger" };

function formatCurrency(n, ccy) {
  return libFormatCurrency(n || 0, ccy || "USD") || "0";
}

function formatErr(err) {
  const data = err?.response?.data;
  if (typeof data === "string") return data;
  if (data?.error) return typeof data.error === "string" ? data.error : data.error.message;
  return err?.message || "Request failed";
}

// Bucket a lead's scheduledAt into Overdue / Today / Upcoming so the screen
// reads top-to-bottom in priority order. Snoozed items push out to the
// snoozedUntil time so they don't sit at the top while they're paused.
function bucketize(items) {
  const now = dayjs();
  const endOfToday = now.endOf("day");
  const buckets = { overdue: [], today: [], upcoming: [] };
  for (const it of items) {
    const fu = it.followUp || {};
    const due = dayjs(fu.snoozedUntil && dayjs(fu.snoozedUntil).isAfter(now) ? fu.snoozedUntil : fu.scheduledAt);
    if (due.isBefore(now)) buckets.overdue.push(it);
    else if (due.isBefore(endOfToday)) buckets.today.push(it);
    else buckets.upcoming.push(it);
  }
  // Sort each bucket by effective due time ascending.
  const sortFn = (a, b) => {
    const aDue = a.followUp?.snoozedUntil && dayjs(a.followUp.snoozedUntil).isAfter(dayjs()) ? a.followUp.snoozedUntil : a.followUp?.scheduledAt;
    const bDue = b.followUp?.snoozedUntil && dayjs(b.followUp.snoozedUntil).isAfter(dayjs()) ? b.followUp.snoozedUntil : b.followUp?.scheduledAt;
    return new Date(aDue).getTime() - new Date(bDue).getTime();
  };
  buckets.overdue.sort(sortFn);
  buckets.today.sort(sortFn);
  buckets.upcoming.sort(sortFn);
  return buckets;
}

function FollowUpRow({ item, onReschedule, orgCurrency }) {
  const complete = useCompleteFollowUp();
  const snooze = useSnoozeFollowUp();
  const clear = useClearFollowUp();

  const fu = item.followUp || {};
  const snoozedActive = fu.snoozedUntil && dayjs(fu.snoozedUntil).isAfter(dayjs());
  const due = dayjs(snoozedActive ? fu.snoozedUntil : fu.scheduledAt);
  const isOverdue = due.isBefore(dayjs());

  const initials = (item.brandName || item.title || "?")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2);

  const handle = async (fn, leadId, successMsg) => {
    try {
      await fn.mutateAsync({ leadId });
      toast.success(successMsg);
    } catch (err) {
      toast.error(formatErr(err));
    }
  };

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "40px 1fr 170px 208px",
        gap: 16,
        alignItems: "center",
        padding: "14px 20px",
        borderBottom: "1px solid var(--color-border-subtle)",
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 9999,
          background: "var(--crm-primary-soft)",
          color: "var(--crm-primary)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 600,
          fontSize: 13,
        }}
      >
        {initials}
      </div>

      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 500, lineHeight: "20px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {item.title}
        </div>
        <div style={{ fontSize: 13, color: "var(--color-text-secondary)", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {item.brandName && <span>{item.brandName}</span>}
          <Badge tone={statusTone[item.status] || "neutral"} dot>{item.status}</Badge>
          {item.value > 0 && (
            <span style={{ fontVariantNumeric: "tabular-nums" }}>{formatCurrency(item.value, item.currency || orgCurrency)}</span>
          )}
          {fu.snoozeCount > 0 && (
            <span style={{ fontSize: 11, color: "var(--crm-warning, rgb(245,158,11))" }}>
              snoozed ×{fu.snoozeCount}
            </span>
          )}
        </div>
      </div>

      <div>
        <div style={{ fontSize: 13, fontWeight: 500, color: isOverdue ? "var(--crm-danger)" : "var(--color-text-primary)" }}>
          {due.format("ddd, D MMM h:mm A")}
        </div>
        <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 2 }}>
          {snoozedActive ? `snoozed · resumes ${due.fromNow()}` : due.fromNow()}
          {fu.scheduledTimezone ? ` · ${fu.scheduledTimezone}` : ""}
        </div>
      </div>

      {/* Reschedule/Clear demote to icon-only — Done/Snooze are the actions
          taken on nearly every row, so they keep their labels. */}
      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        <Button
          variant="primary"
          size="sm"
          leftIcon={<Check size={13} />}
          onClick={() => handle(complete, item._id, "Marked done")}
          disabled={complete.isPending}
        >
          Done
        </Button>
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<Moon size={13} />}
          onClick={() => handle(snooze, item._id, "Snoozed 15 min")}
          disabled={snooze.isPending}
        >
          Snooze
        </Button>
        <Tooltip title="Reschedule" mouseEnterDelay={0.2}>
          <button
            type="button"
            aria-label="Reschedule"
            onClick={() => onReschedule(item)}
            style={iconBtnStyle}
            onMouseEnter={(e) => { e.currentTarget.style.background = "var(--color-bg-muted)"; e.currentTarget.style.borderColor = "var(--color-border-subtle)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = "transparent"; }}
          >
            <Pencil size={14} />
          </button>
        </Tooltip>
        <Tooltip title="Clear follow-up" mouseEnterDelay={0.2}>
          <button
            type="button"
            aria-label="Clear follow-up"
            onClick={() => handle(clear, item._id, "Follow-up cleared")}
            disabled={clear.isPending}
            style={{ ...iconBtnStyle, color: "var(--crm-danger)", opacity: clear.isPending ? 0.5 : 1, cursor: clear.isPending ? "not-allowed" : "pointer" }}
            onMouseEnter={(e) => { if (clear.isPending) return; e.currentTarget.style.background = "var(--color-bg-muted)"; e.currentTarget.style.borderColor = "var(--color-border-subtle)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = "transparent"; }}
          >
            <Trash2 size={14} />
          </button>
        </Tooltip>
      </div>
    </div>
  );
}

const iconBtnStyle = {
  width: 28, height: 28, borderRadius: 6,
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  background: "transparent", border: "1px solid transparent",
  color: "var(--color-text-secondary)", cursor: "pointer",
  transition: "background 120ms, border-color 120ms", flexShrink: 0,
};

function Section({ title, items, tone, onReschedule, orgCurrency }) {
  if (items.length === 0) return null;
  return (
    <div style={{ background: "var(--color-bg-surface)", border: "1px solid var(--color-border-subtle)", borderRadius: 8, overflow: "hidden", marginBottom: 16 }}>
      <div
        style={{
          padding: "10px 20px",
          fontSize: 11,
          fontWeight: 600,
          color: tone === "danger" ? "var(--crm-danger)" : tone === "primary" ? "var(--crm-primary)" : "var(--color-text-secondary)",
          borderBottom: "1px solid var(--color-border-subtle)",
          background: tone === "danger" ? "var(--crm-danger-soft)" : tone === "primary" ? "var(--crm-primary-soft)" : "var(--color-bg-muted)",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span>{title}</span>
        <span style={{ fontSize: 11, color: "var(--color-text-secondary)", fontWeight: 600 }}>· {items.length}</span>
      </div>
      {items.map((it) => (
        <FollowUpRow key={it._id} item={it} onReschedule={onReschedule} orgCurrency={orgCurrency} />
      ))}
    </div>
  );
}

export default function FollowUps() {
  const [view, setView] = useState("email");
  const [scope, setScope] = useState("mine");
  const [page, setPage] = useState(0);
  const [rescheduleLead, setRescheduleLead] = useState(null);
  const qc = useQueryClient();
  const { currency: orgCurrency } = useOrgCurrency();
  const { data, isLoading, isError, error, refetch, isFetching } = usePendingFollowUps({
    scope,
    limit: PAGE_SIZE,
    skip: page * PAGE_SIZE,
  });
  const items = data?.items || [];
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const buckets = useMemo(() => bucketize(items), [items]);
  const onChangeScope = (id) => { setScope(id); setPage(0); };

  // The email view keeps its own queries inside EmailFollowUpsPanel, so refresh
  // there means invalidating the follow-up queries rather than one refetch.
  const emailFetching = useIsFetching({ queryKey: ["crm", "followups", "obligations"] }) > 0;
  const busy = view === "email" ? emailFetching : isFetching;
  const refresh = () => {
    if (view === "email") qc.invalidateQueries({ queryKey: ["crm", "followups", "obligations"] });
    else refetch();
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <PageHeader
        title={view === "email" ? "Email follow-up monitoring" : "Pending follow-ups"}
        actions={(
          <Button
            variant="secondary" size="sm"
            leftIcon={<RefreshCw size={14} className={busy ? "animate-spin" : ""} />}
            onClick={refresh}
            disabled={busy}
          >
            {busy ? "Refreshing…" : "Refresh"}
          </Button>
        )}
        tabs={VIEW_TABS.map((tab) => {
          const Icon = tab.icon;
          const active = view === tab.id;
          return (
            <button key={tab.id} onClick={() => setView(tab.id)} style={{
              position: "relative", height: 34, padding: "0 9px", border: "none", background: "transparent",
              color: active ? "var(--color-text-primary)" : "var(--color-text-secondary)",
              fontSize: 13, fontWeight: active ? 500 : 400, cursor: "pointer",
              display: "inline-flex", alignItems: "center", gap: 6,
            }}>
              <Icon size={14} /> {tab.label}
              {active && <span style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 2, background: "var(--color-text-secondary)" }} />}
            </button>
          );
        })}
        topRight={SCOPE_OPTIONS.map((opt) => (
          <button
            key={opt.id}
            onClick={() => onChangeScope(opt.id)}
            className="tg-tool"
            style={opt.id === scope ? { background: "var(--color-hover-bg)", color: "var(--color-text-primary)", fontWeight: 500 } : undefined}
          >
            {opt.label}
          </button>
        ))}
      />

      {view === "email" ? (
        <EmailFollowUpsPanel scope={scope} />
      ) : (
        <>

      <div style={{ flex: 1, padding: "20px 24px", overflowY: "auto" }}>
        {isLoading && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: 40, justifyContent: "center", color: "var(--color-text-secondary)" }}>
            <Loader2 size={18} className="animate-spin" /> Loading follow-ups…
          </div>
        )}
        {isError && (
          <div style={{ background: "var(--crm-danger-soft)", color: "var(--crm-danger)", padding: 16, borderRadius: 8, display: "flex", alignItems: "flex-start", gap: 12 }}>
            <AlertCircle size={18} style={{ flexShrink: 0, marginTop: 2 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Couldn't load follow-ups</div>
              <div style={{ fontSize: 13, opacity: 0.85 }}>{formatErr(error)}</div>
            </div>
          </div>
        )}
        {!isLoading && !isError && items.length === 0 && (
          <div style={{ padding: 40, textAlign: "center", color: "var(--color-text-secondary)" }}>
            <div style={{ fontSize: 15, marginBottom: 6 }}>All clear — no pending follow-ups{scope === "mine" ? " assigned to you" : " in this org"}.</div>
            <div style={{ fontSize: 13 }}>Schedule one from any lead drawer using the bell icon.</div>
          </div>
        )}
        {!isLoading && !isError && items.length > 0 && (
          <>
            <Section title="Overdue" items={buckets.overdue} tone="danger" onReschedule={setRescheduleLead} orgCurrency={orgCurrency} />
            <Section title="Today" items={buckets.today} tone="primary" onReschedule={setRescheduleLead} orgCurrency={orgCurrency} />
            <Section title="Upcoming" items={buckets.upcoming} tone="neutral" onReschedule={setRescheduleLead} orgCurrency={orgCurrency} />
            <TableFooter
              page={page + 1} pages={pageCount} total={total}
              pageSize={PAGE_SIZE} shown={items.length} label="follow-ups"
              onChange={(p) => setPage(p - 1)}
            />
          </>
        )}
      </div>

      <FollowUpScheduler
        open={!!rescheduleLead}
        lead={rescheduleLead}
        onClose={() => setRescheduleLead(null)}
      />
        </>
      )}
    </div>
  );
}
